import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { PageHeader, EmptyState } from "@/components/Shared";
import api, { formatApiError } from "@/lib/api";
import { TL, formatDate, todayISO, ACCOUNT_TYPE_LABEL, TXN_KIND_LABEL } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Plus, ArrowLeft, Trash2, LockOpen, Lock, CalendarClock } from "lucide-react";
import { toast } from "sonner";

const GELIR_CATEGORIES = [
  "Rezervasyon Ödemesi",
  "Kapora",
  "Ek Hizmet Geliri (Restoran, Spa, Transfer)",
  "Cari Tahsilat",
  "Faiz / Kur Farkı",
  "Diğer Gelir",
];

const GIDER_CATEGORIES = [
  "Personel Maaş",
  "Kira",
  "Elektrik / Su / Doğalgaz",
  "İnternet / Telefon",
  "Vergi ve Harçlar",
  "Malzeme / Sarf",
  "Bakım Onarım",
  "Komisyon Ödemesi",
  "Pazarlama / Reklam",
  "Cari Ödeme",
  "Diğer Gider",
];

function AccountTxnForm({ accountId, onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cariler, setCariler] = useState([]);
  const [direction, setDirection] = useState("gelir"); // "gelir" | "gider"
  const [form, setForm] = useState({
    date: todayISO(),
    kind: "gelir",
    amount: "",
    description: "",
    cari_id: "",
    category: "",
    customCategory: false,
  });

  useEffect(() => {
    if (open) api.get("/cariler").then((r) => setCariler(r.data)).catch(() => {});
  }, [open]);

  const needsCari = form.kind === "tahsilat" || form.kind === "odeme";
  const categories = direction === "gelir" ? GELIR_CATEGORIES : GIDER_CATEGORIES;

  const switchDirection = (dir) => {
    setDirection(dir);
    setForm((f) => ({
      ...f,
      kind: dir === "gelir" ? "gelir" : "gider",
      cari_id: "",
      category: "",
      customCategory: false,
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    // Defensive: ensure a valid kind is always sent — fall back to direction default
    const kind = form.kind || (direction === "gelir" ? "gelir" : "gider");
    setSaving(true);
    try {
      const payload = {
        account_id: accountId,
        date: form.date,
        kind,
        amount: parseFloat(form.amount),
        description: form.description,
        category: form.category,
      };
      if ((kind === "tahsilat" || kind === "odeme") && form.cari_id) payload.cari_id = form.cari_id;
      await api.post("/hesap-hareketler", payload);
      toast.success("Hareket eklendi");
      setOpen(false);
      setDirection("gelir");
      setForm({ date: todayISO(), kind: "gelir", amount: "", description: "", cari_id: "", category: "", customCategory: false });
      onCreated?.();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="add-account-txn-button">
          <Plus className="w-4 h-4 mr-2" /> Yeni Hareket
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-heading">Kasa/Banka Hareketi</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-2" data-testid="direction-toggle">
            <button
              type="button"
              onClick={() => switchDirection("gelir")}
              data-testid="direction-gelir"
              className={`py-3 px-4 rounded-md border text-sm font-medium transition-colors ${direction === "gelir"
                ? "bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] border-transparent"
                : "bg-background text-foreground/70 border-border hover:border-[hsl(var(--success))]/40"}`}
            >
              + Gelir
            </button>
            <button
              type="button"
              onClick={() => switchDirection("gider")}
              data-testid="direction-gider"
              className={`py-3 px-4 rounded-md border text-sm font-medium transition-colors ${direction === "gider"
                ? "bg-destructive text-destructive-foreground border-transparent"
                : "bg-background text-foreground/70 border-border hover:border-destructive/40"}`}
            >
              − Gider
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>İşlem Tarihi</Label>
              <Input required type="date" value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="account-txn-date" />
              <div className="text-[11px] text-foreground/50">Geçmiş tarih girebilirsiniz.</div>
            </div>
            <div className="space-y-2">
              <Label>Tür</Label>
              <Select key={`kind-${direction}`} value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="account-txn-kind"><SelectValue placeholder="Tür seçin" /></SelectTrigger>
                <SelectContent>
                  {direction === "gelir" ? (
                    <>
                      <SelectItem value="gelir">Gelir (genel)</SelectItem>
                      <SelectItem value="tahsilat">Tahsilat (cariden)</SelectItem>
                    </>
                  ) : (
                    <>
                      <SelectItem value="gider">Gider (genel)</SelectItem>
                      <SelectItem value="odeme">Ödeme (cariye)</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            {needsCari && (
              <div className="col-span-2 space-y-2">
                <Label>Cari Hesap</Label>
                <Select value={form.cari_id} onValueChange={(v) => setForm({ ...form, cari_id: v })}>
                  <SelectTrigger data-testid="account-txn-cari"><SelectValue placeholder="Cari seçin" /></SelectTrigger>
                  <SelectContent>
                    {cariler.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="col-span-2 space-y-2">
              <Label>Kategori</Label>
              {form.customCategory ? (
                <div className="flex gap-2">
                  <Input value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    data-testid="account-txn-category-custom" placeholder="Kendi kategori adınız" />
                  <Button type="button" variant="outline"
                    onClick={() => setForm({ ...form, customCategory: false, category: "" })}>
                    Listeden seç
                  </Button>
                </div>
              ) : (
                <Select
                  key={`category-${direction}`}
                  value={form.category || "__none__"}
                  onValueChange={(v) => {
                    if (v === "__custom__") setForm({ ...form, customCategory: true, category: "" });
                    else setForm({ ...form, category: v === "__none__" ? "" : v });
                  }}
                >
                  <SelectTrigger data-testid="account-txn-category"><SelectValue placeholder="Kategori seçin" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Kategori yok</SelectItem>
                    <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-foreground/40">
                      {direction === "gelir" ? "Gelir Kategorileri" : "Gider Kategorileri"}
                    </div>
                    {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    <SelectItem value="__custom__">+ Yeni kategori ekle</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="col-span-2 space-y-2">
              <Label>Tutar (₺)</Label>
              <Input required type="number" step="0.01" min="0.01" value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                data-testid="account-txn-amount" placeholder="0.00" />
            </div>

            <div className="col-span-2 space-y-2">
              <Label>Açıklama</Label>
              <Textarea rows={2} value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="account-txn-desc" />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Vazgeç</Button>
            <Button type="submit" disabled={saving || (needsCari && !form.cari_id)}
              className={direction === "gelir"
                ? "bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] hover:opacity-90"
                : "bg-destructive text-destructive-foreground hover:bg-destructive/90"}
              data-testid="account-txn-submit">
              {saving ? "Kaydediliyor..." : (direction === "gelir" ? "Gelir Kaydet" : "Gider Kaydet")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function HesapDetailPage() {
  const { id } = useParams();
  const [account, setAccount] = useState(null);
  const [items, setItems] = useState([]);

  const load = async () => {
    const [a, b] = await Promise.all([
      api.get(`/hesaplar/${id}`),
      api.get(`/hesaplar/${id}/hareketler`),
    ]);
    setAccount(a.data);
    setItems(b.data);
  };
  useEffect(() => { load().catch(console.error); }, [id]);

  const remove = async (t) => {
    if (!window.confirm("Hareketi silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/hesap-hareketler/${t.id}`);
      toast.success("Silindi");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const releaseBlock = async (t) => {
    try {
      await api.post(`/hesap-hareketler/${t.id}/blokeyi-ac`);
      toast.success("Bloke kaldırıldı — tutar bakiyeye eklendi");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkDate, setBulkDate] = useState(todayISO());
  const [bulkSaving, setBulkSaving] = useState(false);
  const bulkRelease = async () => {
    setBulkSaving(true);
    try {
      const { data } = await api.post("/hesap-hareketler/toplu-blokeyi-ac", {
        account_id: id, until_date: bulkDate,
      });
      toast.success(`${data.released_count} işlem serbest bırakıldı`);
      setBulkOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBulkSaving(false); }
  };

  if (!account) return <Layout><div className="text-sm text-foreground/60">Yükleniyor...</div></Layout>;

  return (
    <Layout>
      <Link to="/hesaplar" className="inline-flex items-center gap-2 text-sm text-foreground/60 hover:text-foreground mb-4" data-testid="back-to-hesaplar">
        <ArrowLeft className="w-4 h-4" /> Hesap Listesi
      </Link>

      <PageHeader
        title={account.name}
        subtitle={`${ACCOUNT_TYPE_LABEL[account.type]}${account.bank_name ? " • " + account.bank_name : ""}${account.iban ? " • " + account.iban : ""}`}
        action={
          <div className="flex items-center gap-2">
            {account.blocked_balance > 0 && (
              <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="bulk-release-button">
                <CalendarClock className="w-4 h-4 mr-2" /> Toplu Blokeyi Aç
              </Button>
            )}
            <AccountTxnForm accountId={id} onCreated={load} />
          </div>
        }
        testId="hesap-detail-header"
      />

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent data-testid="bulk-release-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Toplu Blokeyi Aç</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-foreground/70">
              Bu tarihe (dahil) kadar tarihi olan tüm <b>bloke</b> işlemler serbest bırakılacak ve tutarları ana bakiyeye eklenecek.
            </div>
            <div className="space-y-2">
              <Label>Serbest Bırakma Tarihi (≤)</Label>
              <Input type="date" value={bulkDate} onChange={(e) => setBulkDate(e.target.value)} data-testid="bulk-release-date" />
            </div>
            <div className="text-xs text-foreground/50">
              Mevcut Blokede Bakiye: <b>{TL(account.blocked_balance || 0)}</b>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)} data-testid="bulk-release-cancel">Vazgeç</Button>
            <Button onClick={bulkRelease} disabled={bulkSaving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="bulk-release-confirm">
              {bulkSaving ? "İşleniyor..." : "Serbest Bırak"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className="border border-border rounded-md p-5 bg-background" data-testid="account-balance-card">
          <div className="text-[11px] uppercase tracking-wider text-foreground/50">Güncel Bakiye</div>
          <div className="font-heading text-4xl font-medium tracking-tight mt-2 tabular-nums">{TL(account.balance)}</div>
          <div className="text-xs text-foreground/60 mt-1">Açılış: {TL(account.opening_balance || 0)}</div>
        </div>
        <div className="border border-border rounded-md p-5 bg-background" data-testid="account-blocked-card">
          <div className="text-[11px] uppercase tracking-wider text-foreground/50 flex items-center gap-1">
            <Lock className="w-3 h-3" /> Blokede Bakiye
          </div>
          <div className={`font-heading text-4xl font-medium tracking-tight mt-2 tabular-nums ${account.blocked_balance > 0 ? "text-amber-700" : "text-foreground/40"}`}>
            {TL(account.blocked_balance || 0)}
          </div>
          <div className="text-xs text-foreground/60 mt-1">
            {account.blocked_balance > 0
              ? "Kredi kartı ödemeleri banka tarafından düşürüldüğünde 'Blokeyi Aç' ile serbest bırakın."
              : "Bloke tutulan bir tutar yok."}
          </div>
        </div>
      </div>

      <div className="border border-border rounded-md bg-background overflow-hidden" data-testid="account-txn-table">
        {items.length === 0 ? (
          <EmptyState title="Henüz hareket yok" testId="account-txn-empty" />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-foreground/70">
              <tr>
                <th className="text-left py-3 px-4 text-[11px] uppercase tracking-wider">Tarih</th>
                <th className="text-left py-3 px-4 text-[11px] uppercase tracking-wider">Tür</th>
                <th className="text-left py-3 px-4 text-[11px] uppercase tracking-wider">Kategori</th>
                <th className="text-left py-3 px-4 text-[11px] uppercase tracking-wider">Cari</th>
                <th className="text-left py-3 px-4 text-[11px] uppercase tracking-wider">Açıklama</th>
                <th className="text-right py-3 px-4 text-[11px] uppercase tracking-wider">Tutar</th>
                <th className="text-right py-3 px-4 text-[11px] uppercase tracking-wider">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {items.map((t) => {
                const isIn = ["gelir", "tahsilat", "transfer_in"].includes(t.kind);
                const blocked = !!t.blocked;
                // Compute estimated valor date (next business day after txn date)
                let valorDate = null;
                if (blocked && t.date) {
                  const d = new Date(t.date);
                  if (!Number.isNaN(d.getTime())) {
                    do { d.setDate(d.getDate() + 1); } while (d.getDay() === 0 || d.getDay() === 6);
                    valorDate = d.toISOString().slice(0, 10);
                  }
                }
                return (
                  <tr key={t.id} className={`border-t border-border ${blocked ? "bg-amber-50/50" : ""}`}>
                    <td className="py-3 px-4 text-foreground/80">
                      {formatDate(t.date)}
                      {blocked && valorDate && (
                        <div className="text-[11px] text-amber-700 mt-0.5" data-testid={`valor-date-${t.id}`}>
                          Tahmini valör: {formatDate(valorDate)}
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1">
                        <span className="bg-secondary text-secondary-foreground px-2 py-0.5 rounded-sm text-xs border border-border">
                          {TXN_KIND_LABEL[t.kind] || t.kind}
                        </span>
                        {blocked && (
                          <span className="bg-amber-100 text-amber-800 border border-amber-200 px-2 py-0.5 rounded-sm text-xs inline-flex items-center gap-1" data-testid={`blocked-badge-${t.id}`}>
                            <Lock className="w-3 h-3" /> Bloke
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-foreground/70 text-xs">{t.category || "—"}</td>
                    <td className="py-3 px-4 text-foreground/70">
                      {t.cari_id ? <Link className="text-primary hover:underline" to={`/cariler/${t.cari_id}`}>{t.cari_name || "—"}</Link> : "—"}
                    </td>
                    <td className="py-3 px-4 text-foreground/80">{t.description || "—"}</td>
                    <td className={`py-3 px-4 text-right tabular-nums font-medium ${blocked ? "text-amber-700" : (isIn ? "text-[hsl(var(--success))]" : "text-destructive")}`}>
                      {isIn ? "+" : "−"} {TL(t.amount)}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="inline-flex items-center gap-1">
                        {blocked && (
                          <button
                            onClick={() => releaseBlock(t)}
                            className="text-amber-700 hover:text-amber-900 transition-colors inline-flex items-center gap-1 px-2 py-1 rounded border border-amber-200 bg-amber-50 hover:bg-amber-100 text-xs"
                            data-testid={`release-block-${t.id}`}
                            title="Blokeyi kaldır — tutar ana bakiyeye aktarılır"
                          >
                            <LockOpen className="w-3 h-3" /> Blokeyi Aç
                          </button>
                        )}
                        <button onClick={() => remove(t)} className="text-foreground/40 hover:text-destructive transition-colors" data-testid={`delete-account-txn-${t.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </Layout>
  );
}
